<template>
  <div class="document-management">
    <div class="page-header">
      <h2>文档管理</h2>
      <p class="subtitle">管理隐私政策和用户协议等文档内容</p>
    </div>

    <!-- 文档列表 -->
    <el-card class="doc-list-card">
      <template #header>
        <div class="card-header">
          <span>文档列表</span>
          <el-button 
            type="primary" 
            size="small" 
            @click="refreshList"
            :loading="loading"
          >
            刷新
          </el-button>
        </div>
      </template>

      <el-table 
        :data="documentList" 
        v-loading="loading"
        @row-click="handleViewDocument"
        style="cursor: pointer;"
      >
        <el-table-column prop="title" label="文档标题" min-width="200" />
        <el-table-column prop="type" label="文档类型" width="180">
          <template #default="{ row }">
            <el-tag :type="getDocTypeTag(row.type)">
              {{ getDocTypeName(row.type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="version" label="当前版本" width="120" />
        <el-table-column prop="updatedAt" label="最后更新时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.updatedAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="{ row }">
            <el-button 
              type="primary" 
              size="small" 
              @click.stop="handleEdit(row)"
            >
              编辑
            </el-button>
            <el-button 
              size="small" 
              @click.stop="handleViewHistory(row)"
            >
              查看历史
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 编辑对话框 -->
    <el-dialog
      v-model="editDialogVisible"
      :title="`编辑 - ${currentDoc.title}`"
      width="90%"
      :close-on-click-modal="false"
      @close="handleCloseEditDialog"
    >
      <el-form :model="editForm" label-width="100px">
        <el-form-item label="文档标题">
          <el-input v-model="editForm.title" placeholder="请输入文档标题" />
        </el-form-item>
        
        <el-form-item label="版本号">
          <el-input 
            v-model="editForm.version" 
            placeholder="例如: 1.0.1"
            style="width: 200px;"
          />
          <span class="version-hint">当前版本: {{ currentDoc.version }}</span>
        </el-form-item>

        <el-form-item label="文档内容">
          <div class="editor-container">
            <!-- 工具栏 -->
            <div class="editor-toolbar">
              <el-button-group>
                <el-button size="small" @click="insertMarkdown('# ')">H1</el-button>
                <el-button size="small" @click="insertMarkdown('## ')">H2</el-button>
                <el-button size="small" @click="insertMarkdown('### ')">H3</el-button>
              </el-button-group>
              <el-button-group>
                <el-button size="small" @click="insertMarkdown('**', '**')">粗体</el-button>
                <el-button size="small" @click="insertMarkdown('*', '*')">斜体</el-button>
                <el-button size="small" @click="insertMarkdown('~~', '~~')">删除线</el-button>
              </el-button-group>
              <el-button-group>
                <el-button size="small" @click="insertMarkdown('- ')">列表</el-button>
                <el-button size="small" @click="insertMarkdown('1. ')">序号</el-button>
                <el-button size="small" @click="insertMarkdown('> ')">引用</el-button>
              </el-button-group>
              <el-button-group>
                <el-button 
                  size="small" 
                  :type="previewMode ? 'primary' : ''"
                  @click="previewMode = !previewMode"
                >
                  {{ previewMode ? '编辑模式' : '预览模式' }}
                </el-button>
              </el-button-group>
            </div>

            <!-- 编辑器区域 -->
            <div class="editor-content" v-show="!previewMode">
              <el-input
                ref="contentEditor"
                v-model="editForm.content"
                type="textarea"
                :rows="20"
                placeholder="请输入Markdown格式的文档内容&#10;&#10;支持的语法：&#10;# 一级标题&#10;## 二级标题&#10;### 三级标题&#10;**粗体** *斜体*&#10;- 列表项&#10;1. 有序列表&#10;> 引用文本"
              />
            </div>

            <!-- 预览区域 -->
            <div class="preview-content" v-show="previewMode">
              <div class="markdown-body" v-html="renderedMarkdown"></div>
            </div>
          </div>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveDocument" :loading="saving">
          保存更新
        </el-button>
      </template>
    </el-dialog>

    <!-- 历史记录对话框 -->
    <el-dialog
      v-model="historyDialogVisible"
      :title="`修改历史 - ${currentDoc.title}`"
      width="70%"
    >
      <el-table 
        :data="historyList" 
        v-loading="loadingHistory"
      >
        <el-table-column type="index" label="序号" width="60" />
        <el-table-column prop="version" label="版本" width="120" />
        <el-table-column prop="updatedAt" label="修改时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.updatedAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100">
          <template #default="{ row }">
            <el-button 
              type="text" 
              size="small"
              @click="handleViewHistoryDetail(row)"
            >
              查看详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="historyList.length === 0 && !loadingHistory" class="empty-history">
        <el-empty description="暂无历史记录" />
      </div>
    </el-dialog>

    <!-- 历史详情对话框 -->
    <el-dialog
      v-model="historyDetailDialogVisible"
      title="历史版本详情"
      width="80%"
    >
      <div v-if="historyDetail" class="history-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="版本号">
            {{ historyDetail.version }}
          </el-descriptions-item>
          <el-descriptions-item label="更新时间">
            {{ formatDate(historyDetail.updatedAt) }}
          </el-descriptions-item>
          <el-descriptions-item label="文档标题" :span="2">
            {{ historyDetail.title }}
          </el-descriptions-item>
        </el-descriptions>

        <div class="history-content">
          <h4>文档内容</h4>
          <div class="markdown-body" v-html="renderMarkdown(historyDetail.content)"></div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'

// 状态定义
const loading = ref(false)
const saving = ref(false)
const loadingHistory = ref(false)
const documentList = ref([])
const historyList = ref([])
const historyDetail = ref(null)

const editDialogVisible = ref(false)
const historyDialogVisible = ref(false)
const historyDetailDialogVisible = ref(false)
const previewMode = ref(false)

const currentDoc = ref({
  type: '',
  title: '',
  version: '',
  content: ''
})

const editForm = reactive({
  title: '',
  version: '',
  content: ''
})

const contentEditor = ref(null)

// 计算属性
const renderedMarkdown = computed(() => {
  return renderMarkdown(editForm.content)
})

// 方法
const getDocTypeName = (type) => {
  const types = {
    'app_info': '应用信息',
    'privacy_policy': '隐私政策',
    'user_agreement': '用户协议'
  }
  return types[type] || type
}

const getDocTypeTag = (type) => {
  const tags = {
    'app_info': 'info',
    'privacy_policy': 'warning',
    'user_agreement': 'success'
  }
  return tags[type] || ''
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 简单的Markdown渲染（可以替换为marked.js等专业库）
const renderMarkdown = (content) => {
  if (!content) return ''
  
  let html = content
  
  // 标题
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>')
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>')
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>')
  
  // 粗体和斜体
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>')
  
  // 删除线
  html = html.replace(/~~(.+?)~~/g, '<del>$1</del>')
  
  // 列表
  html = html.replace(/^\- (.+)$/gim, '<li>$1</li>')
  html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
  
  // 有序列表
  html = html.replace(/^\d+\. (.+)$/gim, '<li>$1</li>')
  
  // 引用
  html = html.replace(/^> (.+)$/gim, '<blockquote>$1</blockquote>')
  
  // 换行
  html = html.replace(/\n/g, '<br>')
  
  return html
}

const insertMarkdown = (prefix, suffix = '') => {
  const textarea = contentEditor.value?.$el?.querySelector('textarea')
  if (!textarea) return
  
  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const selectedText = editForm.content.substring(start, end)
  
  const newText = prefix + selectedText + suffix
  editForm.content = 
    editForm.content.substring(0, start) +
    newText +
    editForm.content.substring(end)
  
  // 设置光标位置
  setTimeout(() => {
    textarea.focus()
    const newPos = start + prefix.length + selectedText.length
    textarea.setSelectionRange(newPos, newPos)
  }, 0)
}

const loadDocumentList = async () => {
  loading.value = true
  try {
    const token = localStorage.getItem('admin_token')
    const response = await axios.get('/api/admin/about/contents', {
      headers: { Authorization: `Bearer ${token}` }
    })
    
    if (response.data.success) {
      documentList.value = response.data.data
    }
  } catch (error) {
    console.error('获取文档列表失败:', error)
    ElMessage.error('获取文档列表失败')
  } finally {
    loading.value = false
  }
}

const refreshList = () => {
  loadDocumentList()
}

const handleViewDocument = (row) => {
  // 点击行查看详情（可选功能）
}

const handleEdit = async (row) => {
  loading.value = true
  try {
    const token = localStorage.getItem('admin_token')
    const response = await axios.get(
      `/api/admin/about/contents/${row.type}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    
    if (response.data.success) {
      const doc = response.data.data
      currentDoc.value = { ...doc }
      
      editForm.title = doc.title
      editForm.version = doc.version
      editForm.content = doc.content
      
      editDialogVisible.value = true
      previewMode.value = false
    }
  } catch (error) {
    console.error('获取文档详情失败:', error)
    ElMessage.error('获取文档详情失败')
  } finally {
    loading.value = false
  }
}

const handleSaveDocument = async () => {
  if (!editForm.title || !editForm.version || !editForm.content) {
    ElMessage.warning('请填写完整的文档信息')
    return
  }
  
  try {
    await ElMessageBox.confirm(
      '确定要更新此文档吗？旧版本将保存到历史记录中。',
      '确认更新',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    
    saving.value = true
    const token = localStorage.getItem('admin_token')
    const response = await axios.put(
      `/api/admin/about/contents/${currentDoc.value.type}`,
      {
        title: editForm.title,
        content: editForm.content,
        version: editForm.version
      },
      { headers: { Authorization: `Bearer ${token}` } }
    )
    
    if (response.data.success) {
      ElMessage.success('文档更新成功')
      editDialogVisible.value = false
      loadDocumentList()
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('更新文档失败:', error)
      ElMessage.error('更新文档失败')
    }
  } finally {
    saving.value = false
  }
}

const handleCloseEditDialog = () => {
  editForm.title = ''
  editForm.version = ''
  editForm.content = ''
  previewMode.value = false
}

const handleViewHistory = async (row) => {
  currentDoc.value = { ...row }
  historyDialogVisible.value = true
  loadingHistory.value = true
  
  try {
    const token = localStorage.getItem('admin_token')
    const response = await axios.get(
      `/api/admin/about/contents/${row.type}/history`,
      { 
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 20 }
      }
    )
    
    if (response.data.success) {
      historyList.value = response.data.data
    }
  } catch (error) {
    console.error('获取历史记录失败:', error)
    ElMessage.error('获取历史记录失败')
  } finally {
    loadingHistory.value = false
  }
}

const handleViewHistoryDetail = async (row) => {
  try {
    const token = localStorage.getItem('admin_token')
    const response = await axios.get(
      `/api/admin/about/history/${row.id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    
    if (response.data.success) {
      historyDetail.value = response.data.data
      historyDetailDialogVisible.value = true
    }
  } catch (error) {
    console.error('获取历史详情失败:', error)
    ElMessage.error('获取历史详情失败')
  }
}

onMounted(() => {
  loadDocumentList()
})
</script>

<style scoped>
.document-management {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0 0 8px 0;
  font-size: 24px;
  color: #303133;
}

.subtitle {
  margin: 0;
  color: #909399;
  font-size: 14px;
}

.doc-list-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.version-hint {
  margin-left: 15px;
  color: #909399;
  font-size: 12px;
}

.editor-container {
  width: 100%;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}

.editor-toolbar {
  background-color: #f5f7fa;
  padding: 10px;
  border-bottom: 1px solid #dcdfe6;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.editor-content,
.preview-content {
  min-height: 500px;
}

.editor-content :deep(textarea) {
  border: none !important;
  box-shadow: none !important;
}

.preview-content {
  padding: 20px;
  background-color: #fff;
  overflow-y: auto;
  max-height: 600px;
}

.markdown-body {
  line-height: 1.8;
  color: #333;
}

.markdown-body h1 {
  font-size: 28px;
  margin: 20px 0 15px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #eee;
}

.markdown-body h2 {
  font-size: 24px;
  margin: 18px 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.markdown-body h3 {
  font-size: 20px;
  margin: 15px 0 10px 0;
}

.markdown-body ul, .markdown-body ol {
  padding-left: 30px;
  margin: 10px 0;
}

.markdown-body li {
  margin: 5px 0;
}

.markdown-body blockquote {
  border-left: 4px solid #42b983;
  padding-left: 15px;
  margin: 15px 0;
  color: #666;
  background-color: #f9f9f9;
  padding: 10px 15px;
}

.markdown-body strong {
  font-weight: 600;
  color: #000;
}

.empty-history {
  padding: 40px 0;
  text-align: center;
}

.history-detail {
  max-height: 70vh;
  overflow-y: auto;
}

.history-content {
  margin-top: 20px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.history-content h4 {
  margin: 0 0 15px 0;
  color: #303133;
}
</style>
